/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { StyleSheet } from 'aphrodite';
import { BrandColors, Text, ActionButton } from '@blend/enterprise-components';
import axios from 'axios';
import { sendENoteSigningEmails } from 'blb-api/ecloseOps';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import * as Sentry from '@sentry/browser';

export const styles = StyleSheet.create({
  snippet: {
    backgroundColor: BrandColors.CLOUD_20,
    borderRadius: 4,
    maxWidth: 700,
    padding: 8,
  },
});

namespace SendEnoteSigningLinkEmail {
  export interface Props {
    closingId: string;
    tenant: string;
  }
}

export const SendENoteSigningLinkEmail: React.FC<SendEnoteSigningLinkEmail.Props> = ({ closingId, tenant }) => {
  const [sent, setSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sendResponse, setSendResponse] = useState<EClose.API.SendENoteSigningLinkEmailsResponse>();
  const Toaster = useToasts();
  const handleSendEnoteSigningUrlEmail = async (): Promise<void> => {
    const confirm = window.confirm(
      'Are you sure that you want to send email to all signers with links to sign their eNotes?',
    );
    if (!confirm) {
      return;
    }
    try {
      setIsLoading(true);
      setSendResponse(await sendENoteSigningEmails(closingId, tenant));
      setSent(true);
    } catch (err) {
      setSent(false);
      let message = 'Fail to send emails with enote signing links';
      if (axios.isAxiosError(err) && err.response) {
        message = err.response.data.error;
      }
      Sentry.captureException(err);
      Toaster?.addToast({
        message,
        theme: 'error',
      });
      setSendResponse(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const renderResponse = () => {
    if (!sendResponse) return;
    if (sendResponse.recipients.length === 0) {
      return (
        <div data-it="send-email-message">
          <Text weight="bold">
            There are no outstanding signers on this eNote. Likely every signer has signed the eNote successfully. No
            emails were sent.
          </Text>
        </div>
      );
    }
    return (
      <div>
        <div>
          <Text weight="bold">
            Emails were sent to multiple borrowers (if applicable) successfully at {new Date().toLocaleTimeString()} for
            signing their eNotes. The links will be valid for {sendResponse.expirationHours} hours. Here are the
            borrower details:
          </Text>
        </div>
        <ul data-it="enote-signing-link-email-recipient-list">
          {sendResponse.recipients.map((recipient, index) => (
            <li data-it={`enote-email-recipient-${index}`} key={recipient.partyId}>
              <div>
                <Text weight="semibold">Email: </Text>
                {recipient.email} | <Text weight="semibold">PartyID: </Text>
                {recipient.partyId} | <Text weight="semibold">Name: </Text>
                {recipient.firstName} {recipient.lastName}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div>
      <ActionButton
        data-it="send-email-enote-signing-link-button"
        disabled={sent}
        isLoading={isLoading}
        label="Send email with eNote signing links"
        onClick={handleSendEnoteSigningUrlEmail}
        theme="primary"
      />
      {renderResponse()}
    </div>
  );
};
